/**
 * shared/space_industry.ts — CLIENT MIRROR of server/src/lib/space_industry.ts
 * (the server copy is authoritative; keep them byte-identical below the
 * headers — the SpacetimeDB module bundle cannot reach across the monorepo).
 */


// ── Deterministic hash (fnv-1a + avalanche — the correlated-salt lesson) ────
function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
export const indH = (s: string): number => fnv(s) / 4294967296;

// ── System tier (byte-mirror of galaxy.ts: danger/richness grow rimward) ────
export function sysTierOf(systemId: string): 1 | 2 | 3 {
  const m = /^sys(\d+)$/.exec(systemId);
  if (!m) return 1; // user-world systems sit in the calm halo
  const t = Number(m[1]) / 100;
  return t < 0.35 ? 1 : t < 0.7 ? 2 : 3;
}

// ── MINING ──────────────────────────────────────────────────────────────────
/** Space ore rides the EXISTING resource families — mined stacks are real
 *  crafting inputs (stat-rolled), and family.commodity bridges them into the
 *  market. Richer seams only occur rimward (minTier). */
export interface SpaceOre {
  key: string; label: string; familyKey: string;
  minTier: 1 | 2 | 3; richness: number; root: string;
}
export const SPACE_ORES: Record<string, SpaceOre> = {
  drift_ferrite:  { key: 'drift_ferrite', label: 'Drift Ferrite', familyKey: 'ferrite_ore', minTier: 1, richness: 1.0, root: 'Duskiron Drift' },
  pale_silica:    { key: 'pale_silica', label: 'Pale Silica', familyKey: 'stone', minTier: 1, richness: 1.0, root: 'Weirstone Shoal' },
  cobalt_shale:   { key: 'cobalt_shale', label: 'Cobalt Shale', familyKey: 'ferrite_ore', minTier: 2, richness: 1.6, root: 'Cobalite Shale' },
  aether_ice:     { key: 'aether_ice', label: 'Aether Ice', familyKey: 'gas', minTier: 2, richness: 1.5, root: 'Frostvapor Pocket' },
  voidglass_lode: { key: 'voidglass_lode', label: 'Voidglass Lode', familyKey: 'crystal', minTier: 3, richness: 2.4, root: 'Voidglass Vein' },
  noble_seam:     { key: 'noble_seam', label: 'Noble Seam', familyKey: 'noble_ore', minTier: 3, richness: 2.2, root: 'Aurelium Seam' },
};
export const MINE_CYCLE_S = 4;        // one beam cycle → one extraction claim
export const MINE_BASE_UNITS = 7;     // per cycle, before richness/roll
export const MINE_CRIT_PCT = 6;       // rare rich pocket: double yield
/** Which ores a system's fields carry (all ores at or below the tier). */
export function oresForTier(tier: 1 | 2 | 3): SpaceOre[] {
  return Object.values(SPACE_ORES).filter((o) => o.minTier <= tier);
}

// ── REFINING ────────────────────────────────────────────────────────────────
export const REFINE_STATION_EFF = 0.35;  // public station line
export const REFINE_BEACON_EFF = 0.5;    // your own deployed refinery does better
export const REFINE_MIN_QTY = 3;
export const REFINE_STAT_BOOST = 1.15;   // refined stock crafts tighter

// ── SALVAGE ─────────────────────────────────────────────────────────────────
/** wreck:<battleCyc>:<side>:(c|f)<idx> — capitals (c) strip far richer. */
export const WRECK_KEY_RE = /^wreck:\d{1,9}:[01]:(c|f)\d{1,3}$/;
export const SALVAGE_CYCLE_S = 2.5;
export const SALVAGE_COMP_PCT = { f: 12, c: 45 } as const; // direct component chance
export const SALVAGE_UNITS = { f: 4, c: 12 } as const;     // scrap resource units
/** THE YARD LAW: a wreck key must name a hull the war yard actually shows. The yard
 *  (SpaceModule's wreck field, sys0) is a deterministic per-cycle timeline — fighter
 *  hulks f0–f79 (side = idx % 2) on the field 8 + 7·idx s into the cycle, capital
 *  hulks c0–c5 (side = idx % 2) at 110 + 110·idx s — plus the hulks a player-killed
 *  capital sheds mid-war (c6 up). Only the CURRENT cycle's yard exists (the one just
 *  past is allowed for a client clock on the boundary). Before this any key the
 *  regex accepted minted scrap and a component chance from nothing. */
export const WRECK_YARD_FIGHTERS = 80;
export const WRECK_YARD_CAPITALS = 6;
export const WRECK_YARD_HULKS_MAX = 64;
export const WRECK_YARD_SLACK_S = 30; // client clocks step (the CLOCK-STEP law) — a hulk may be cut a little early
export function wreckOnYard(key: string, nowS: number, periodS: number): boolean {
  const m = /^wreck:(\d{1,9}):([01]):(c|f)(\d{1,3})$/.exec(key);
  if (!m) return false;
  const cyc = Number(m[1]), side = Number(m[2]), idx = Number(m[4]);
  const cur = Math.floor(nowS / periodS);
  if (cyc !== cur && cyc !== cur - 1) return false;
  const intoS = cyc === cur ? nowS - cur * periodS : Infinity;
  if (m[3] === 'f') return idx < WRECK_YARD_FIGHTERS && side === idx % 2 && intoS + WRECK_YARD_SLACK_S >= 8 + idx * 7;
  if (idx < WRECK_YARD_CAPITALS) return side === idx % 2 && intoS + WRECK_YARD_SLACK_S >= 110 + idx * 110;
  return idx < WRECK_YARD_HULKS_MAX;
}

// ── EXPLORATION: hidden signatures ─────────────────────────────────────────
export const SIG_KEY_RE = /^sig:(sys\d{1,3}|w\d{1,20}):[0-2]:\d{1,9}$/;
export type SigKind = 'cache' | 'derelict' | 'relic';
export interface SpaceSig {
  key: string; kind: SigKind; level: 1 | 2 | 3;
}
/** THE HIDDEN-POSITION LAW: a site's seat lives ONLY on the server (space_sig,
 *  hashed on the shard's secret salt) and reaches a pilot through sig_reveal —
 *  three scanner rounds (bearing + range, then the seat). Wormhole throats too. */
export const SCAN_COOLDOWN_S = 5;
/** A recovery (spaceScanLoot) needs the streamed position within this of the seat. */
export const SIG_RECOVER_RANGE_M = 3_000;
/** The hidden key of a wormhole's throat inside one endpoint system. */
export function whHiddenKey(whKey: string, systemId: string): string { return `${whKey}@${systemId}`; }
export const WH_HIDDEN_KEY_RE = /^wh:\d{1,9}:\d@(sys\d{1,3})$/;
/** Signatures re-seed every 4 hours. */
export function scanEpoch(nowMs: number): number {
  return Math.floor(nowMs / (4 * 3600_000));
}
/** 1–3 hidden signatures per system per epoch — deterministic for everyone. */
export function sigsOf(systemId: string, epoch: number): SpaceSig[] {
  const n = 1 + (fnv(`${systemId}|sigN|${epoch}`) % 3);
  const tier = sysTierOf(systemId);
  const out: SpaceSig[] = [];
  for (let i = 0; i < n; i++) {
    const s = `${systemId}|sig${i}|${epoch}`;
    const kinds: SigKind[] = ['cache', 'cache', 'derelict', 'derelict', 'relic'];
    const kind = kinds[fnv(s + 'k') % kinds.length];
    out.push({ key: `sig:${systemId}:${i}:${epoch}`, kind, level: tier });
  }
  return out;
}
export const SIG_CACHE_SOV = 120n;       // × level, paid on the ledger
export const SIG_SCAN_CYCLES = 3;        // narrowing rounds before warpable

// ── WORMHOLES (temporary free crossings; counterpoint to warp + courses) ───
export interface Wormhole { key: string; a: string; b: string }
/** 3 deterministic pairs per scan epoch among the prebuilt spiral. */
export function wormholesOf(epoch: number): Wormhole[] {
  const out: Wormhole[] = [];
  for (let i = 0; i < 3; i++) {
    const s = `wh|${epoch}|${i}`;
    const a = 1 + (fnv(s + 'a') % 100);
    let b = 1 + (fnv(s + 'b') % 100);
    if (b === a) b = (b % 100) + 1;
    out.push({ key: `wh:${epoch}:${i}`, a: i === 0 && fnv(s + 'c') % 12 === 0 ? 'sys0' : `sys${a}`, b: `sys${b}` });
  }
  return out;
}


// ── WORMHOLE LIVING (tier-1 wave 2026-08-29): every hole carries hash-derived
// mass limits, a lifetime inside its 4 h epoch, and a system EFFECT while it
// stands. PURE — the server validates transits against the same numbers the
// client renders (wormholeFlux rows carry the accumulated mass). ──
export interface WormholeAttrs {
  /** Total mass the hole passes before collapse (Mkg). */
  massBudget: number;
  /** Largest single ship it admits (Mkg) — some holes are frigate-only. */
  maxShipMass: number;
  /** Seconds after the epoch start the hole is born, and how long it lives. */
  bornOffsetS: number;
  lifetimeS: number;
  /** '' | 'pulsar' (+shields) | 'magnetar' (+damage) | 'cataclysm' (−capacitor)
   *  — applies to ships in EITHER endpoint system while the hole stands. */
  effect: '' | 'pulsar' | 'magnetar' | 'cataclysm';
}

/** Ship wet mass (Mkg) per chassis — the wormhole currency. */
export const CHASSIS_MASS: Record<string, number> = {
  ship_lancer: 9, ship_talon: 7, ship_courier: 14, ship_bulwark: 25, ship_sovereign: 60,
  ship_wren: 6, ship_packet: 10, ship_pathfinder: 13, ship_kestrel: 5, ship_warden: 16,
  ship_prospector: 15, ship_gleaner: 20, ship_harrier: 10, ship_vesper: 8, ship_farstrider: 16,
  ship_arbalest: 22, ship_drover: 32, ship_tender: 28, ship_delver: 48, ship_laurel: 22,
  ship_rime: 18, ship_ironclad: 34, ship_bastion: 30, ship_argosy: 70, ship_reclaimer: 52,
};

/** Shrink stages: ≥50 % consumed = SHRUNK (visibly tighter), ≥90 % = CRITICAL. */
export const WH_SHRUNK_PCT = 50;
export const WH_CRITICAL_PCT = 90;

export function whAttrsOf(whKey: string): WormholeAttrs {
  const h = (s: string): number => fnv(`${whKey}|${s}`);
  const budget = 60 + (h('mass') % 141);            // 60–200 Mkg total
  const frigateOnly = h('small') % 4 === 0;         // 1 in 4 holes excludes heavies
  const effects: WormholeAttrs['effect'][] = ['', '', '', 'pulsar', 'magnetar', 'cataclysm'];
  return {
    massBudget: budget,
    maxShipMass: frigateOnly ? 14 : 60,
    bornOffsetS: (h('born') % 90) * 60,             // born within the first 90 min
    lifetimeS: (120 + (h('life') % 121)) * 60,      // lives 2–4 h
    effect: effects[h('fx') % effects.length],
  };
}

/** The effect a system feels right now (Space Wave 1): the first LIVE
 *  wormhole touching it (wormholesOf order) with a non-empty effect. Applied
 *  by shipStats on the client; the server reads it when it needs the same
 *  answer. */
export function systemEffectOf(systemId: string, nowMs: number): WormholeAttrs['effect'] {
  const epoch = scanEpoch(nowMs);
  const ageS = (nowMs - epoch * 4 * 3600_000) / 1000;
  for (const w of wormholesOf(epoch)) {
    if (w.a !== systemId && w.b !== systemId) continue;
    const at = whAttrsOf(w.key);
    if (ageS < at.bornOffsetS || ageS > at.bornOffsetS + at.lifetimeS) continue;
    if (at.effect) return at.effect;
  }
  return '';
}

// ── REGIONAL MARKETS ───────────────────────────────────────────────────────
/** Prices drift per (station, commodity) on a 2 h epoch; core systems sell
 *  basics cheap, rim stations pay premiums — hauling is the arbitrage. */
export function marketEpoch(nowMicros: bigint): number {
  return Number(nowMicros / 7_200_000_000n);
}
export const MARKET_SPREAD = 0.12;        // buy above / sell below the local mark
export const MARKET_PRICE_SCALE = 4;      // space credits over ground baseValue
export function stationDrift(stationKey: string, commodity: string, epoch: number, tier: 1 | 2 | 3, comTier: string): number {
  let d = 0.72 + indH(`${stationKey}|${commodity}|${epoch}`) * 0.83; // 0.72..1.55
  if (tier === 3 && (comTier === 'raw' || comTier === 'staple')) d *= 1.25; // rim pays for basics
  if (tier === 1 && (comTier === 'raw' || comTier === 'staple')) d *= 0.85; // core is glutted
  if (tier === 3 && comTier === 'luxury') d *= 0.9;                          // rim mines its own luxuries
  return d;
}
export function marketUnitPrice(baseValue: number, drift: number, side: 'buy' | 'sell'): bigint {
  const mark = baseValue * MARKET_PRICE_SCALE * drift;
  const px = side === 'buy' ? mark * (1 + MARKET_SPREAD) : mark * (1 - MARKET_SPREAD);
  return BigInt(Math.max(1, Math.round(px)));
}
export const MARKET_MAX_QTY = 200;

// ── SOVEREIGNTY ────────────────────────────────────────────────────────────
/** Every charted system leans to a civ by default; the war ledger shifts it. */
export function baseControlOf(systemId: string): 'aurelian' | 'borean' | 'commons' {
  if (systemId === 'sys0') return 'commons';
  return fnv(systemId + '|sov') % 2 === 0 ? 'aurelian' : 'borean';
}
export const SOV_SHIFT_CLAMP = 100;
export const SOV_CONTRIB_COOLDOWN_S = 45;
export const SOV_CONTRIB_POINTS = 2;
/** score > +20 → aurelian · score < −20 → borean · else the base lean. */
export function controlFromScore(systemId: string, score: number): 'aurelian' | 'borean' | 'commons' {
  if (systemId === 'sys0') return 'commons';
  if (score > 20) return 'aurelian';
  if (score < -20) return 'borean';
  return baseControlOf(systemId);
}

// ── INCURSIONS ─────────────────────────────────────────────────────────────
/** Every 6 h epoch, two mid/rim systems fall under a raider occupation. */
export function incursionEpoch(nowMs: number): number {
  return Math.floor(nowMs / (6 * 3600_000));
}
export interface Incursion { systemId: string; bossKey: string; key: string }
export function incursionsOf(epoch: number): Incursion[] {
  const out: Incursion[] = [];
  for (let i = 0; i < 2; i++) {
    const n = 30 + (fnv(`inc|${epoch}|${i}`) % 71); // sys30..sys100
    out.push({ systemId: `sys${n}`, bossKey: `inc:${epoch}:${i}:boss`, key: `inc:${epoch}:${i}` });
  }
  return out;
}
export const INCURSION_SPAWN_MULT = 3;

// ── FITTING BUDGETS (CPU / powergrid — can't max every slot) ───────────────
export const FIT_BUDGET: Record<string, { cpu: number; pg: number }> = {
  ship_lancer: { cpu: 180, pg: 140 },
  ship_talon: { cpu: 230, pg: 190 },
  ship_courier: { cpu: 200, pg: 210 },
  ship_bulwark: { cpu: 260, pg: 280 },
  ship_sovereign: { cpu: 330, pg: 340 },
  ship_wren: { cpu: 190, pg: 120 },
  ship_packet: { cpu: 170, pg: 160 },
  ship_pathfinder: { cpu: 280, pg: 170 },
  ship_kestrel: { cpu: 170, pg: 150 },
  ship_warden: { cpu: 230, pg: 230 },
  ship_prospector: { cpu: 210, pg: 200 },
  ship_gleaner: { cpu: 210, pg: 220 },
  ship_harrier: { cpu: 250, pg: 220 },
  ship_vesper: { cpu: 260, pg: 200 },
  ship_farstrider: { cpu: 270, pg: 230 },
  ship_arbalest: { cpu: 290, pg: 260 },
  ship_drover: { cpu: 220, pg: 260 },
  ship_tender: { cpu: 230, pg: 270 },
  ship_delver: { cpu: 230, pg: 300 },
  ship_laurel: { cpu: 260, pg: 260 },
  ship_rime: { cpu: 280, pg: 240 },
  ship_ironclad: { cpu: 280, pg: 330 },
  ship_bastion: { cpu: 310, pg: 290 },
  ship_argosy: { cpu: 240, pg: 320 },
  ship_reclaimer: { cpu: 260, pg: 310 },
};
export const FIT_COST: Record<string, { cpu: number; pg: number }> = {
  reactor: { cpu: 20, pg: 10 },
  engine: { cpu: 25, pg: 30 },
  capacitor: { cpu: 22, pg: 18 },
  shield: { cpu: 30, pg: 32 },
  armor: { cpu: 16, pg: 34 },
  weapon: { cpu: 34, pg: 30 },
  missile: { cpu: 26, pg: 22 },
  counter: { cpu: 18, pg: 10 },
  droidport: { cpu: 24, pg: 12 },
  cargo: { cpu: 10, pg: 16 },
};
/** Better gear draws more — quality 0..1000 scales draw 0.7×..1.3×. */
export function fitCost(slot: string, quality: number): { cpu: number; pg: number } {
  const base = FIT_COST[slot] ?? { cpu: 20, pg: 20 };
  const m = 0.7 + Math.max(0, Math.min(1000, quality)) / 1000 * 0.6;
  return { cpu: Math.round(base.cpu * m), pg: Math.round(base.pg * m) };
}

// ── DEPLOYABLES ────────────────────────────────────────────────────────────
export const STRUCT_KINDS = ['depot', 'refinery'] as const;
export const STRUCT_MAX_PER_OWNER = 2;
export const BEACON_RANGE_M = 2600;   // refine/restock validated against the row's coords

// ── CUSTODY (Space Wave 2) ─────────────────────────────────────────────────
/** A hull's hold holds chassis.cargo × this many units (+ the cargo comp's
 *  round(q/1000 × 20) × this). Lancer 100 · Talon 50 · Courier 600 ·
 *  Bulwark 200 · Sovereign 1,000. */
export const HOLD_UNITS_PER_CARGO = 25;
/** Strip a player wreck from this close; wrecks fade after this long. */
export const WRECK_STRIP_RANGE_M = 1_500;
export const WRECK_TTL_S = 7_200;
